import axios from 'axios';

/**
 * @typedef {object} KbArticle
 * @property {number}   id
 * @property {string}   title
 * @property {string}   overview      - HTML string
 * @property {string}   body          - HTML string
 * @property {number}   createdBy
 * @property {object}   author
 * @property {number}   author.id     - same as createdBy
 * @property {string}   author.name
 * @property {string}   coverImageUrl
 * @property {number[]} categories    - see kbCategory.id
 * @property {string[]} termTypes     - see term.type
 * @property {number[]} paths         - see path.id
 * @property {number[]} software      - see app.id
 * @property {number[]} tags          - see tag.id
 * @property {string}   updatedAt
 */

/**
 * @typedef {object} KbArticleBrief
 * @property {number}   id
 * @property {string}   title
 * @property {string}   overview      - HTML string
 * @property {number}   createdBy
 * @property {object}   author
 * @property {number}   author.id     - same as createdBy
 * @property {string}   author.name
 * @property {string}   coverImageUrl
 * @property {number[]} categories    - see kbCategory.id
 * @property {string[]} termTypes     - see term.type
 * @property {number[]} paths         - see path.id
 * @property {number[]} software      - see app.id
 * @property {number[]} tags          - see kbTag.id
 * @property {string}   updatedAt
 */

/**
 * @typedef {object} KbCategory
 * @property {number} id
 * @property {string} label
 * @property {string} name
 * @property {string} imageUrl
 * @property {number} articleCount
 */

/**
 * @typedef {object} KbFeedback
 * @property {number} id
 * @property {string} urlPath
 * @property {string} experience - 'positive' | 'negative'
 * @property {string} text
 * @property {number} createdBy
 * @property {object} author
 * @property {number} author.id - same as createdBy
 * @property {string} author.name
 */

/**
 * @typedef {object} KbTag
 * @property {number} id
 * @property {string} name
 */

export default {
    /**
     * @async
     * @param {object}   fields
     * @param {number}   fields.createdBy
     * @param {string}   fields.title
     * @param {number[]} fields.categories
     * @param {string[]} fields.termTypes
     * @param {number[]} fields.paths
     * @param {object[]} [fields.tags]
     * @param {number[]} [fields.tags[].id] - tag will be auto-created if omitted
     * @param {string[]} fields.tags[].name
     * @param {string}   fields.overview
     * @param {string}   fields.coverImageUrl
     * @param {string}   fields.body - HTML string
     * @returns {KbArticle}
     */
    async createArticle(fields) {
        const options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/kb-articles',
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.article;
    },

    /**
     * @async
     * @param {object} [options]
     * @param {number} [options.category]
     * @returns {KbArticleBrief[]}
     */
    async getArticles(options = {}) {
        options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-articles',
            params: {
                categories: options.category ? [options.category] : null,
                brief: true,
                // cache: 'kbarticles'
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.articles;
    },

    /**
     * @async
     * @param {object}   options
     * @param {string}   [options.search]
     * @param {number[]} [options.categories]
     * @param {string[]} [options.termTypes]
     * @param {number[]} [options.paths]
     * @param {number[]} [options.software]
     * @param {number[]} [options.tags]
     * @returns {KbArticleBrief[]}
     */
    async searchArticles(options) {
        
        options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-articles',
            params: options,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };

        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.articles;
    },

    /**
     * @async
     * @param {number} articleId
     * @returns {KbArticle}
     */
    async getArticle(articleId) {
        const options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-articles/' + articleId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.article;
    },

    /**
     * @async
     * @param {number}   articleId
     * @param {object}   fields
     * @param {string}   [fields.title]
     * @param {number[]} [fields.categories]
     * @param {string[]} [fields.terms]
     * @param {number[]} [fields.paths]
     * @param {object[]} [fields.tags]
     * @param {number[]} [fields.tags[].id] - tag will be auto-created if omitted
     * @param {string[]} fields.tags[].name
     * @param {string}   [fields.overview]
     * @param {string}   [fields.coverImageUrl]
     * @param {string}   [fields.body] - HTML string
     */
    async updateArticle(articleId, fields) {
        const options = {
            method: 'PATCH',
            baseURL: window.apiUrl,
            url: '/kb-articles/' + articleId,
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
    },

    /**
     * @async
     * @param {number} articleId
     */
    async deleteArticle(articleId) {
        const options = {
            method: 'DELETE',
            baseURL: window.apiUrl,
            url: '/kb-articles/' + articleId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
    },

    /**
     * @async
     * @param {number} articleId
     */
    async createArticleView(articleId) {
        const options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/kb-articles/' + articleId + '/views',
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
    },

    /**
     * @async
     * @returns {KbCategory[]}
     */
    async getCategories() {
        
        //?cache=kbcategories
        const options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-categories',
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.categories;
    },

    /**
     * @async
     * @param {object} fields
     * @param {number} fields.createdBy
     * @param {string} fields.urlPath
     * @param {string} fields.experience - 'positive' | 'negative'
     * @param {string} [fields.text]
     * @returns {KbFeedback}
     */
    async createFeedback(fields) {
        if (['positive', 'negative'].indexOf(fields.experience) === -1) {
            throw new Error('unknown kb feedback experience:', fields.experience);
        }
        const options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/kb-feedback',
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
        return res.data.feedback;
    },

    /**
     * @async
     * @param {number} feedbackId
     * @param {object} fields
     * @param {string} [fields.experience] - 'positive' | 'negative'
     * @param {string} [fields.text]
     */
    async updateFeedback(feedbackId, fields) {
        if (fields.experience) {
            if (['positive', 'negative'].indexOf(fields.experience) === -1) {
                throw new Error('unknown kb feedback experience:', fields.experience);
            }
        }
        const options = {
            method: 'PATCH',
            baseURL: window.apiUrl,
            url: '/kb-feedback/' + feedbackId,
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        const res = await axios.request(options);
        if (!res.data.success) {
            throw new Error(res.data.error);
        }
    },

    /**
     * @async
     * @param {number} tagId
     * @returns {KbTag}
     */
    getTag(tagId) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-tags/' + tagId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.tag;
        });
    },

    /**
     * @async
     * @param {string} searchTerm
     * @returns {KbTag[]}
     */
    searchTags(searchTerm) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/kb-tags',
            params: {
                q: searchTerm
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.tags;
        });
    },
};



// WEBPACK FOOTER //
// ./src/services/knowledge-base/index.js